import { createSlice } from '@reduxjs/toolkit';
import { ResponseStatus } from 'src/pages/MainPage/api/booleanSearch/types';
import { apiRequest } from 'src/shared/api/api';
import { RequestStatus, UrlAPI } from 'src/shared/api/types';
import { wait } from 'src/shared/lib/async';
import { createAppAsyncThunk } from '../../../../app/redux/createAction';
import { GetBoardAndExecRES, GetGeneralRES, GetPerformanceRES, GetSelectedDataRES, GetSelectedMetricsRES, NameWithStringValue } from './resTypes';

const NAME = 'database';

// * Async thunks
export const getGeneral = createAppAsyncThunk(`${NAME}/getGeneral`, async (arg: { params: { id: string } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<GetGeneralRES>({
		url: `${UrlAPI.company}/general`,
		params,
		thunkAPI,
		signal,
	});
});

export const getPerformance = createAppAsyncThunk(`${NAME}/getPerformance`, async (arg: { params: { id: string } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<GetPerformanceRES>({
		url: `${UrlAPI.company}/performance`,
		params,
		thunkAPI,
		signal,
	});
});

export const getBoardAndExec = createAppAsyncThunk(`${NAME}/getBoardAndExec`, async (arg: { params: { id: string } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<GetBoardAndExecRES>({
		url: `${UrlAPI.company}/boardAndExec`,
		params,
		thunkAPI,
		signal,
	});
});

export const getSelectedData = createAppAsyncThunk(`${NAME}/getSelectedData`, async (arg: { params: { id: string } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<GetSelectedDataRES>({
		url: `${UrlAPI.company}/selectedData`,
		params,
		thunkAPI,
		signal,
	});
});

export const getDataset = createAppAsyncThunk(`${NAME}/getDataset`, async (arg: { params: { id: string } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<NameWithStringValue[]>({
		url: `${UrlAPI.company}/dataset`,
		params,
		thunkAPI,
		signal,
	});
});

export const getSelectedMetrics = createAppAsyncThunk(`${NAME}/getSelectedMetrics`, async (arg: { id: string; dataPoints: string[] }, thunkAPI) => {
	const { id, dataPoints } = arg;
	const { signal } = thunkAPI;

	let res;
	let status: ResponseStatus = null;
	let queryId: number | null = null;

	do {
		res = await apiRequest.postRequest<GetSelectedMetricsRES>({
			url: 'companies',
			payload:
				queryId !== null
					? { queryId }
					: {
							columnsWithPeriods: dataPoints.map((dataPointId) => ({
								associatedDataTakenFromCompany: false,
								datapointId: +dataPointId,
								reportingPeriodId: null,
							})),
							ids: [+id],
						},
			thunkAPI,
			signal,
		});

		queryId = res?.queryId;
		status = res?.status;

		if (res?.queryId && res.status === 'InProgress') {
			await wait(3000);
		}
	} while (status === 'InProgress');

	return res.result;
});

// * Reducer
export interface State {
	general: {
		data: GetGeneralRES | null;
		status: RequestStatus;
	};
	performance: {
		data: GetPerformanceRES | null;
		status: RequestStatus;
	};
	boardAndExec: {
		data: GetBoardAndExecRES | null;
		status: RequestStatus;
	};
	selectedData: {
		data: GetSelectedDataRES | null;
		status: RequestStatus;
	};
	dataset: {
		data: NameWithStringValue[] | null;
		status: RequestStatus;
	};
	selectedMetrics: {
		data: GetSelectedMetricsRES['result'] | null;
		status: RequestStatus;
	};
	pdfImages: Record<string, string> | null;
	imagesLoading: number;
	menuOption: number;
}

export const initialState: State = {
	general: {
		data: null,
		status: RequestStatus.still,
	},
	performance: {
		data: null,
		status: RequestStatus.still,
	},
	boardAndExec: {
		data: null,
		status: RequestStatus.still,
	},
	selectedData: {
		data: null,
		status: RequestStatus.still,
	},
	dataset: {
		data: null,
		status: RequestStatus.still,
	},
	selectedMetrics: {
		data: null,
		status: RequestStatus.still,
	},
	pdfImages: null,
	imagesLoading: 0,
	menuOption: 1,
};

export const slice = createSlice({
	name: NAME,
	initialState,
	reducers: {
		imageLoadStart: (state) => {
			state.imagesLoading = state.imagesLoading + 1;
		},
		imageSave: (state, action: { payload: { title: string; image: string } }) => {
			const { title, image } = action.payload;

			state.pdfImages = {
				...state.pdfImages,
				[title]: image,
			};
			state.imagesLoading = state.imagesLoading - 1;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getGeneral.pending, (state) => {
			state.general.status = RequestStatus.loading;
		});
		builder.addCase(getGeneral.fulfilled, (state, action) => {
			state.general.status = RequestStatus.still;
			state.general.data = action.payload;
		});
		builder.addCase(getGeneral.rejected, (state) => {
			state.general.status = RequestStatus.failed;
		});

		builder.addCase(getPerformance.pending, (state) => {
			state.performance.status = RequestStatus.loading;
		});
		builder.addCase(getPerformance.fulfilled, (state, action) => {
			state.performance.status = RequestStatus.still;
			state.performance.data = action.payload;
		});
		builder.addCase(getPerformance.rejected, (state) => {
			state.performance.status = RequestStatus.failed;
		});

		builder.addCase(getBoardAndExec.pending, (state) => {
			state.boardAndExec.status = RequestStatus.loading;
		});
		builder.addCase(getBoardAndExec.fulfilled, (state, action) => {
			state.boardAndExec.status = RequestStatus.still;
			state.boardAndExec.data = action.payload;
		});
		builder.addCase(getBoardAndExec.rejected, (state) => {
			state.boardAndExec.status = RequestStatus.failed;
		});

		builder.addCase(getSelectedData.pending, (state) => {
			state.selectedData.status = RequestStatus.loading;
		});
		builder.addCase(getSelectedData.fulfilled, (state, action) => {
			state.selectedData.status = RequestStatus.still;
			state.selectedData.data = action.payload;
		});
		builder.addCase(getSelectedData.rejected, (state) => {
			state.selectedData.status = RequestStatus.failed;
		});

		builder.addCase(getDataset.pending, (state) => {
			state.dataset.status = RequestStatus.loading;
		});
		builder.addCase(getDataset.fulfilled, (state, action) => {
			state.dataset.status = RequestStatus.still;
			state.dataset.data = action.payload;
		});
		builder.addCase(getDataset.rejected, (state) => {
			state.dataset.status = RequestStatus.failed;
		});

		builder.addCase(getSelectedMetrics.pending, (state) => {
			state.selectedMetrics.status = RequestStatus.loading;
		});
		builder.addCase(getSelectedMetrics.fulfilled, (state, action) => {
			state.selectedMetrics.status = RequestStatus.still;
			state.selectedMetrics.data = action.payload;
		});
		builder.addCase(getSelectedMetrics.rejected, (state) => {
			state.selectedMetrics.status = RequestStatus.failed;
		});
	},
});

export const actionsDatabase = {
	...slice.actions,
	getGeneral,
	getPerformance,
	getBoardAndExec,
	getSelectedData,
	getDataset,
	getSelectedMetrics,
};
